import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalIcon,
  Clock, MapPin, Download, Trash2, Edit2, X, Users
} from 'lucide-react';

const EVENT_COLORS = {
  visit: '#3b82f6', meeting: '#8b5cf6', call: '#22c55e',
  task: '#f59e0b', default: '#6366f1',
};
const VIEW_MODES = ['month', 'week', 'day'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getMonthDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  const startDay = first.getDay();
  // Previous month filler
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }
  // Current month
  for (let i = 1; i <= last.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }
  // Next month filler
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
  }
  return days;
}

function getWeekDays(date) {
  const d = new Date(date);
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const dd = new Date(start);
    dd.setDate(start.getDate() + i);
    days.push(dd);
  }
  return days;
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getHours() {
  const hrs = [];
  for (let h = 7; h <= 21; h++) {
    hrs.push(`${String(h).padStart(2, '0')}:00`);
  }
  return hrs;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({
    title: '', date: '', time: '09:00', duration: 60,
    type: 'meeting', customer_name: '', location_text: '', notes: '', agenda: '',
  });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const start = formatDate(new Date(y, m - 1, 1));
    const end = formatDate(new Date(y, m + 2, 0));
    try {
      const res = await api.get('/calendar/events', { params: { start, end } });
      setEvents(res.data.events || []);
    } catch { toast.error('Failed to load calendar'); }
    setLoading(false);
  }, [currentDate]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const getEventsForDate = (dateStr) => events.filter(e => e.date === dateStr);

  const handleCreateEvent = async () => {
    if (!form.title || !form.date) { toast.error('Title and date required'); return; }
    try {
      await api.post('/calendar/events', form);
      toast.success('Meeting created');
      setShowCreate(false);
      resetForm();
      fetchEvents();
    } catch { toast.error('Failed to create event'); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/calendar/events/${eventId}`);
      toast.success('Event deleted');
      setSelectedEvent(null);
      fetchEvents();
    } catch { toast.error('Failed to delete'); }
  };

  const downloadIcal = (eventId) => {
    const token = localStorage.getItem('token');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/events/${eventId}/ical?token=${token}`, '_blank');
  };

  const downloadAllIcal = () => {
    const token = localStorage.getItem('token');
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const start = formatDate(new Date(y, m, 1));
    const end = formatDate(new Date(y, m + 1, 0));
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/export/ical?start=${start}&end=${end}&token=${token}`, '_blank');
  };

  const resetForm = () => setForm({ title: '', date: '', time: '09:00', duration: 60, type: 'meeting', customer_name: '', location_text: '', notes: '', agenda: '' });

  const openCreate = (dateStr) => {
    resetForm();
    setForm(prev => ({ ...prev, date: dateStr || formatDate(new Date()) }));
    setShowCreate(true);
  };

  const today = formatDate(new Date());

  // Render helpers
  const renderMonthView = () => {
    const days = getMonthDays(currentDate.getFullYear(), currentDate.getMonth());
    return (
      <div className="grid grid-cols-7 border-l border-t border-gray-200">
        {DAYS.map(d => (
          <div key={d} className="text-xs font-semibold text-gray-500 text-center py-2 border-b border-r border-gray-200 bg-gray-50">
            {d}
          </div>
        ))}
        {days.map(({ date, isCurrentMonth }, idx) => {
          const dateStr = formatDate(date);
          const dayEvents = getEventsForDate(dateStr);
          const isToday = dateStr === today;
          return (
            <div
              key={idx}
              className={`min-h-[100px] border-b border-r border-gray-200 p-1 cursor-pointer transition-colors hover:bg-blue-50/30 ${
                !isCurrentMonth ? 'bg-gray-50/50' : 'bg-white'
              }`}
              onClick={() => openCreate(dateStr)}
              data-testid={`calendar-day-${dateStr}`}
            >
              <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                isToday ? 'bg-blue-600 text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-400'
              }`}>
                {date.getDate()}
              </div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map(ev => (
                  <div
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                    className="text-[10px] leading-tight truncate rounded px-1 py-0.5 cursor-pointer hover:opacity-80"
                    style={{ background: `${EVENT_COLORS[ev.type] || EVENT_COLORS.default}18`, color: EVENT_COLORS[ev.type] || EVENT_COLORS.default, borderLeft: `2px solid ${EVENT_COLORS[ev.type] || EVENT_COLORS.default}` }}
                    data-testid={`event-${ev.id}`}
                  >
                    {ev.time && <span className="font-medium">{ev.time} </span>}
                    {ev.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const days = getWeekDays(currentDate);
    const hours = getHours();
    return (
      <div className="overflow-auto border border-gray-200 rounded-lg" style={{ maxHeight: '600px' }}>
        <div className="grid grid-cols-8 sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="text-xs text-gray-400 p-2 border-r border-gray-200" />
          {days.map(d => {
            const dateStr = formatDate(d);
            const isToday = dateStr === today;
            return (
              <div key={dateStr} className={`text-center p-2 border-r border-gray-200 ${isToday ? 'bg-blue-50' : ''}`}>
                <div className="text-xs text-gray-500">{DAYS[d.getDay()]}</div>
                <div className={`text-sm font-semibold ${isToday ? 'text-blue-600' : 'text-gray-800'}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {hours.map(hour => (
          <div key={hour} className="grid grid-cols-8 border-b border-gray-100">
            <div className="text-[10px] text-gray-400 p-1 border-r border-gray-200 text-right pr-2">{hour}</div>
            {days.map(d => {
              const dateStr = formatDate(d);
              const dayEvents = getEventsForDate(dateStr).filter(e => e.time && e.time.startsWith(hour.slice(0, 2)));
              return (
                <div
                  key={dateStr}
                  className="min-h-[40px] border-r border-gray-100 p-0.5 cursor-pointer hover:bg-blue-50/30"
                  onClick={() => openCreate(dateStr)}
                >
                  {dayEvents.map(ev => (
                    <div
                      key={ev.id}
                      onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                      className="text-[10px] rounded px-1 py-0.5 truncate cursor-pointer mb-0.5"
                      style={{ background: EVENT_COLORS[ev.type] || EVENT_COLORS.default, color: 'white' }}
                    >
                      {ev.title}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderDayView = () => {
    const dateStr = formatDate(currentDate);
    const dayEvents = getEventsForDate(dateStr);
    const hours = getHours();
    return (
      <div className="border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: '600px' }}>
        <div className="bg-blue-50 p-3 text-center border-b border-gray-200 sticky top-0 z-10">
          <div className="text-lg font-semibold text-gray-800">{DAYS[currentDate.getDay()]}, {currentDate.getDate()} {MONTHS[currentDate.getMonth()]}</div>
          <div className="text-xs text-gray-500">{dayEvents.length} events</div>
        </div>
        {hours.map(hour => {
          const hourEvents = dayEvents.filter(e => e.time && e.time.startsWith(hour.slice(0, 2)));
          return (
            <div key={hour} className="flex border-b border-gray-100 min-h-[50px]">
              <div className="w-16 text-xs text-gray-400 p-2 text-right border-r border-gray-200 flex-shrink-0">{hour}</div>
              <div className="flex-1 p-1 cursor-pointer hover:bg-blue-50/30" onClick={() => openCreate(dateStr)}>
                {hourEvents.map(ev => (
                  <div
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(ev); }}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 mb-1 cursor-pointer text-white text-sm"
                    style={{ background: EVENT_COLORS[ev.type] || EVENT_COLORS.default }}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{ev.title}</div>
                      <div className="text-xs opacity-80">{ev.customer_name} {ev.duration ? `(${ev.duration}min)` : ''}</div>
                    </div>
                    <Badge className="bg-white/20 text-white text-[10px]">{ev.type}</Badge>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const headerText = view === 'month'
    ? `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    : view === 'week'
    ? `Week of ${formatDate(getWeekDays(currentDate)[0])}`
    : `${DAYS[currentDate.getDay()]}, ${currentDate.getDate()} ${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="calendar-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Calendar</h1>
            <p className="text-sm text-gray-500">Manage meetings, visits, and field activities</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={downloadAllIcal} data-testid="export-ical-btn">
              <Download className="w-4 h-4 mr-1" /> Export .ics
            </Button>
            <Button size="sm" onClick={() => openCreate(formatDate(new Date()))} data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-1" /> New Meeting
            </Button>
          </div>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)} data-testid="cal-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="text-xs" onClick={goToday}>Today</Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(1)} data-testid="cal-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <span className="font-semibold text-gray-800 text-sm ml-2">{headerText}</span>
              </div>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                {VIEW_MODES.map(v => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${view === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid={`view-${v}`}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        <Card>
          <CardContent className="p-2">
            {loading ? (
              <div className="h-[400px] flex items-center justify-center text-gray-400">Loading calendar...</div>
            ) : (
              <>
                {view === 'month' && renderMonthView()}
                {view === 'week' && renderWeekView()}
                {view === 'day' && renderDayView()}
              </>
            )}
          </CardContent>
        </Card>

        {/* Event type legend */}
        <div className="flex items-center gap-4 text-xs flex-wrap">
          {Object.entries(EVENT_COLORS).filter(([k]) => k !== 'default').map(([type, color]) => (
            <span key={type} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
              <span className="text-gray-500 capitalize">{type}</span>
            </span>
          ))}
        </div>

        {/* Create Event Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CalIcon className="w-5 h-5 text-blue-600" />New Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Input placeholder="Meeting title *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="event-date-input" />
                <Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} data-testid="event-time-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select className="h-9 rounded-md border border-gray-200 px-3 text-sm bg-white" value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="event-type-select">
                  <option value="meeting">Meeting</option>
                  <option value="visit">Visit</option>
                  <option value="call">Call</option>
                  <option value="task">Task</option>
                </select>
                <Input type="number" placeholder="Duration (min)" value={form.duration} onChange={e => setForm({...form, duration: parseInt(e.target.value) || 60})} />
              </div>
              <Input placeholder="Client name" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} data-testid="event-customer-input" />
              <Input placeholder="Location" value={form.location_text} onChange={e => setForm({...form, location_text: e.target.value})} />
              <textarea className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" rows={2} placeholder="Notes / agenda" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreateEvent} data-testid="save-event-btn">Create Meeting</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Event Detail Dialog */}
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="max-w-sm">
            {selectedEvent && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm" style={{ background: EVENT_COLORS[selectedEvent.type] || EVENT_COLORS.default }} />
                    {selectedEvent.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <CalIcon className="w-4 h-4" />
                    <span>{selectedEvent.date}</span>
                    {selectedEvent.time && <><Clock className="w-4 h-4 ml-2" /><span>{selectedEvent.time}</span></>}
                    {selectedEvent.duration && <span className="text-gray-400">({selectedEvent.duration}min)</span>}
                  </div>
                  {selectedEvent.customer_name && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4" /> <span>{selectedEvent.customer_name}</span>
                    </div>
                  )}
                  {(selectedEvent.location_text || selectedEvent.customer_location) && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{selectedEvent.location_text || `${selectedEvent.customer_location?.lat?.toFixed(4)}, ${selectedEvent.customer_location?.lng?.toFixed(4)}`}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Badge className="capitalize text-xs" style={{ background: `${EVENT_COLORS[selectedEvent.type] || EVENT_COLORS.default}18`, color: EVENT_COLORS[selectedEvent.type] || EVENT_COLORS.default }}>{selectedEvent.type}</Badge>
                    <Badge variant="outline" className="capitalize text-xs">{selectedEvent.status}</Badge>
                    <Badge variant="outline" className="text-xs">{selectedEvent.source}</Badge>
                  </div>
                  {selectedEvent.notes && <p className="text-gray-500 text-xs bg-gray-50 rounded-lg p-2">{selectedEvent.notes}</p>}
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadIcal(selectedEvent.id)} data-testid="download-ical-btn">
                    <Download className="w-3 h-3 mr-1" /> .ics
                  </Button>
                  {selectedEvent.source === 'calendar' && (
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteEvent(selectedEvent.id)} data-testid="delete-event-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
